/**
 * L'écran des litiges — la liste de travail de ce qu'il reste à régler.
 *
 * Cadrage « Stafa v2 — Étape 8 : les litiges », validé le 05/09/2026. Maquette
 * `docs/maquettes/litiges.html`.
 *
 * Même découpe que partout : `VueLitiges` dessine, `EcranLitiges` décide, et le
 * calcul est dans `litiges.ts`, en fonctions pures.
 *
 *
 * TROIS CHOIX QUI SE VOIENT
 * ---------------------------------------------------------------------------
 *
 *   - **les OUVERTS par défaut.** C'est une liste de travail, pas un journal.
 *     Les résolus restent accessibles par le filtre : on cherche parfois ce qui
 *     avait été convenu il y a six mois ;
 *
 *   - **le décompte est la moitié de l'écran.** « 4 litiges ouverts — le plus
 *     ancien depuis 73 jours · 3 ordres bloqués en facturation, 6 030,00 € HT ».
 *     Sans la seconde moitié, la liste n'est qu'un pense-bête ; avec elle,
 *     c'est de l'argent qui attend. Il vient de l'API, compté sur TOUS les
 *     ouverts — un filtre ne doit pas faire croire que la somme a baissé ;
 *
 *   - **la colonne Ordre dit le statut de l'ORDRE**, pas celui du litige. Le
 *     cadrage insiste : « un OT peut être livré et en litige ». Les deux ne se
 *     confondent pas, et c'est justement leur écart qui bloque une facture.
 *
 *
 * CET ÉCRAN OUVRE LA RÉSOLUTION, IL NE LA REFAIT PAS
 * ---------------------------------------------------------------------------
 * La boîte vient de `EcranOrdres.tsx`, où elle sert déjà au bandeau de la carte
 * d'étape. Deux boîtes qui demandent la même chose divergeraient au premier
 * champ ajouté.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  lireLitiges,
  lireMotifsLitige,
  type DecompteLitiges,
  type EchecApi,
  type Litige,
} from './api.js';
import { BoiteResolution } from './EcranOrdres.js';
import { resoudreUnLitige } from './api.js';
import { ageDit, decompteLitiges, delaiMoyen, etapeDite } from './litiges.js';
import {
  classeStatut,
  enEuros,
  enFrancais,
  statutDit,
  type MotifLitige,
} from './ot.js';

export type EtatLitiges =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly litiges: readonly Litige[];
      readonly decompte: DecompteLitiges;
    };

export type EtatFiltre = 'ouverts' | 'resolus' | 'tous';

function Absent() {
  return <span className="absent">—</span>;
}

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export interface ProprietesVueLitiges {
  readonly etat: EtatLitiges;
  readonly filtre: EtatFiltre;
  readonly motif: string;
  readonly recherche: string;
  readonly motifs: readonly MotifLitige[];
  readonly resolution: {
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandFiltre: (filtre: EtatFiltre) => void;
  readonly quandMotif: (motif: string) => void;
  readonly quandRecherche: (texte: string) => void;
  readonly quandResoudre: (litigeId: string) => void;
  readonly quandChampResolution: (
    cle: 'texte' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandEnregistrerResolution: () => void;
  readonly quandFermerResolution: () => void;
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function VueLitiges(p: ProprietesVueLitiges) {
  const litiges = p.etat.nom === 'prete' ? p.etat.litiges : [];
  const moyen = delaiMoyen(litiges);

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {p.resolution !== null && (
          <BoiteResolution
            saisie={p.resolution}
            envoi={p.envoi}
            refus={p.refus}
            quandChamp={p.quandChampResolution}
            quandFermer={p.quandFermerResolution}
            quandEnregistrer={p.quandEnregistrerResolution}
          />
        )}

        <div className="barre-outils">
          <input
            className="champ recherche"
            type="search"
            placeholder="N° d’OT, client, site, ce qui s’est passé…"
            value={p.recherche}
            onChange={(e) => {
              p.quandRecherche(e.target.value);
            }}
          />
          <label className="champ-date">
            <span className="etiquette">Motif</span>
            <select
              className="champ"
              value={p.motif}
              onChange={(e) => {
                p.quandMotif(e.target.value);
              }}
            >
              <option value="">Tous</option>
              {p.motifs.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="champ-date">
            <span className="etiquette">État</span>
            <select
              className="champ"
              value={p.filtre}
              onChange={(e) => {
                p.quandFiltre(e.target.value as EtatFiltre);
              }}
            >
              <option value="ouverts">Ouverts</option>
              <option value="resolus">Résolus</option>
              <option value="tous">Tous</option>
            </select>
          </label>
        </div>

        {p.etat.nom === 'chargement' && <p className="attente">Chargement…</p>}
        {p.etat.nom === 'panne' && (
          <p className="bandeau-refus">La liste des litiges n’a pas abouti.</p>
        )}

        {p.etat.nom === 'prete' && (
          <>
            {/* LA PHRASE QUI FAIT L'ÉCRAN. Elle porte sur TOUS les ouverts, y
                compris ceux que le filtre cache : l'argent bloqué ne diminue
                pas parce qu'on regarde ailleurs. */}
            <p className="decompte">
              {decompteLitiges(p.etat.decompte)}
              {p.filtre !== 'ouverts' && moyen !== null && (
                <span className="sourdine">
                  {' '}
                  · délai moyen de résolution : {moyen} jour{moyen > 1 ? 's' : ''}
                </span>
              )}
            </p>

            {litiges.length === 0 ? (
              <div className="vide">
                <p>
                  {p.filtre === 'ouverts'
                    ? 'Aucun litige ouvert. Ils s’ouvrent depuis la fiche d’un ordre, sur l’étape où l’incident a eu lieu.'
                    : 'Aucun litige ne correspond à ces filtres.'}
                </p>
              </div>
            ) : (
              <div className="tableau-cadre">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">N° d’OT</th>
                      <th scope="col" className="col-souple">
                        Client
                      </th>
                      <th scope="col" className="col-large col-souple">
                        Étape
                      </th>
                      <th scope="col">Motif</th>
                      <th scope="col" className="col-large">
                        Ce qui s’est passé
                      </th>
                      <th scope="col">Ouvert le</th>
                      <th scope="col">Depuis</th>
                      <th scope="col">Ordre</th>
                      {p.filtre !== 'ouverts' && <th scope="col">Réglé</th>}
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {litiges.map((l) => {
                      const age = ageDit(l.jours_ouvert);
                      return (
                        <tr
                          className="rangee"
                          key={l.id}
                          tabIndex={0}
                          onClick={() => {
                            p.quandOuvrirOrdre(l.ot_id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              p.quandOuvrirOrdre(l.ot_id);
                            }
                          }}
                        >
                          <td className="code">{l.ot_numero}</td>
                          <td className="col-souple">{l.client}</td>
                          <td className="col-large col-souple">
                            <span className="note-litige" title={etapeDite(l)}>
                              {etapeDite(l)}
                            </span>
                          </td>
                          <td>{l.motif ?? <Absent />}</td>
                          {/* LA NOTE EST COUPÉE À LA LARGEUR DE SA COLONNE,
                              et le texte entier reste en infobulle. MESURÉ le
                              07/09/2026 : sans cette borne, le tableau
                              débordait de 53 px de son cadre en 1280 px de
                              fenêtre — une note est du texte libre, elle n'a
                              aucune longueur maximale. */}
                          <td className="col-large">
                            {l.note === null ? (
                              <Absent />
                            ) : (
                              <span className="note-litige" title={l.note}>
                                {l.note}
                              </span>
                            )}
                          </td>
                          <td className="code">
                            {enFrancais(l.ouvert_le.slice(0, 10)) ?? <Absent />}
                            <span className="secondaire">{l.ouvert_par}</span>
                          </td>
                          {/* « DEPUIS » EST UN NOMBRE DE JOURS, pas une date à
                              soustraire de tête, et LE CHIFFRE porte le rouge —
                              pas un pavé derrière lui (cadrage §7). Un litige
                              résolu n'a plus d'âge : il a un délai. */}
                          <td className={`code age-${age.couleur}`}>
                            {l.resolu_le === null ? age.texte : <Absent />}
                          </td>
                          <td>
                            <span className={`statut ${classeStatut(l.ot_statut)}`}>
                              {statutDit(l.ot_statut)}
                            </span>
                          </td>
                          {p.filtre !== 'ouverts' && (
                            <td>
                              {l.resolu_le === null ? (
                                <Absent />
                              ) : (
                                <>
                                  <span className="code">
                                    {enFrancais(l.resolu_le.slice(0, 10))}
                                  </span>
                                  {l.resolution !== null && (
                                    <span className="secondaire">{l.resolution}</span>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                          <td className="actions-liste">
                            {l.resolu_le === null && (
                              <button
                                className="lien-action"
                                type="button"
                                disabled={p.envoi}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  p.quandResoudre(l.id);
                                }}
                              >
                                Résoudre
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {p.etat.decompte.ouverts > 0 && p.filtre === 'ouverts' && (
              <p className="aide-saisie">
                Un litige ouvert bloque la facturation de son ordre — c'est le
                premier des trois blocages du cadrage, avec la commande client
                manquante et l'accord tarifaire en attente. Le montant ci-dessus
                est le prix de vente de ces ordres :{' '}
                {enEuros(p.etat.decompte.montant) ?? '—'} HT.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Ce litige n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  invalide: 'La résolution est obligatoire : dites comment le litige a été réglé.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Des fiches s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Une étape déjà validée ne se déplace plus.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

export interface ProprietesLitiges {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function EcranLitiges({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesLitiges) {
  const [etat, setEtat] = useState<EtatLitiges>({ nom: 'chargement' });
  const [filtre, setFiltre] = useState<EtatFiltre>('ouverts');
  const [motif, setMotif] = useState('');
  const [recherche, setRecherche] = useState('');
  const [motifs, setMotifs] = useState<readonly MotifLitige[]>([]);
  const [resolution, setResolution] = useState<{
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /** Le référentiel des motifs, chargé UNE FOIS : il ne bouge pas d'un filtre à l'autre. */
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lus = await lireMotifsLitige(j);
      if (!abandonne && lus.etat === 'ok') setMotifs(lus.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  const relire = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lus = await lireLitiges(j, { etat: filtre, motif, q: recherche });
    if (lus.etat === 'ok') {
      setEtat({ nom: 'prete', ...lus.valeur });
    } else {
      if (lus.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
    }
  }, [avecJeton, filtre, motif, recherche, quandAccesPerdu]);

  useEffect(() => {
    let abandonne = false;
    setEtat({ nom: 'chargement' });
    // LA RECHERCHE ATTEND QU'ON AIT FINI DE TAPER — 250 ms. Sans ce délai,
    // chaque lettre partirait à l'API, et les réponses reviendraient dans le
    // désordre.
    const minuteur = setTimeout(() => {
      if (!abandonne) void relire();
    }, 250);
    return () => {
      abandonne = true;
      clearTimeout(minuteur);
    };
  }, [relire]);

  const enregistrer = () => {
    if (resolution === null) return;
    if (resolution.texte.trim() === '') {
      setRefus('Dites comment le litige a été réglé.');
      return;
    }
    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resoluLe =
        resolution.jour === '' || resolution.heure === ''
          ? null
          : new Date(`${resolution.jour}T${resolution.heure}:00`).toISOString();
      const fait = await resoudreUnLitige(j, resolution.litigeId, {
        resolution: resolution.texte.trim(),
        ...(resoluLe === null ? {} : { resolu_le: resoluLe }),
      });
      setEnvoi(false);
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        setRefus(REFUS[fait.cause]);
        return;
      }
      setResolution(null);
      await relire();
    })();
  };

  return (
    <VueLitiges
      etat={etat}
      filtre={filtre}
      motif={motif}
      recherche={recherche}
      motifs={motifs}
      resolution={resolution}
      envoi={envoi}
      refus={refus}
      quandFiltre={setFiltre}
      quandMotif={setMotif}
      quandRecherche={setRecherche}
      quandResoudre={(litigeId) => {
        const maintenant = new Date();
        const deuxChiffres = (n: number) => String(n).padStart(2, '0');
        setRefus(null);
        setResolution({
          litigeId,
          texte: '',
          jour: `${String(maintenant.getFullYear())}-${deuxChiffres(
            maintenant.getMonth() + 1,
          )}-${deuxChiffres(maintenant.getDate())}`,
          heure: `${deuxChiffres(maintenant.getHours())}:${deuxChiffres(
            maintenant.getMinutes(),
          )}`,
        });
      }}
      quandChampResolution={(cle, valeur) => {
        setResolution((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: valeur },
        );
      }}
      quandEnregistrerResolution={enregistrer}
      quandFermerResolution={() => {
        setResolution(null);
        setRefus(null);
      }}
      quandOuvrirOrdre={quandOuvrirOrdre}
    />
  );
}
